import React, { useEffect, useState } from "react";

import {
  getLocalStorageCropExplorerPseudoUserId,
  setLocalStorageCountry,
  setLocalStorageHectare,
  setLocalStorageZipCode,
  useDeleteCropExploration,
  useTranslation,
  useUpdateCropExploration,
  useUpdateCropExplorationTemplate,
} from "utils";

import { CropExplorationResponse, CropExplorationTemplateResponse } from "models";

import { ContextMenu, Flex, Form, Icon, InputWithAddonAfter, Modal } from "components";

import { CropExplorerTimeline } from "pages/app/crop-explorer/components/CropExplorerTimeline";
import { HectareModal } from "pages/app/crop-explorer/components/HectareModal";
import { ZipCodeModal } from "pages/app/crop-explorer/components/ZipCodeModal";

const CropExplorerContextMenu = ({
  visible,
  onRename,
  onDelete,
  handleShowContextMenu,
  t,
}: {
  visible: boolean;
  onRename: () => void;
  onDelete: () => void;
  handleShowContextMenu: (visible: boolean) => void;
  t: (key: string) => string;
}) => (
  <ContextMenu
    items={[
      {
        label: `${t("project.contextMenu.renameProject")}`,
        iconName: "tabler/edit",
        onClick: onRename,
      },
      {
        label: `${t("project.contextMenu.deleteProject")}`,
        iconName: "tabler/trash",
        onClick: onDelete,
        state: "warning",
      },
    ]}
    visible={visible}
    onClose={() => handleShowContextMenu(false)}
    placement="bottom-left"
    iconPlacement="left"
    dataTestId="farm-select-context-menu"
  >
    <div data-testid="farm-actions-context-menu-icon" onClick={() => handleShowContextMenu(!visible)}>
      <Icon iconName="tabler/dots-vertical" className="pointer right" size="m" stroke="#100C06" fill="#100C06" />
    </div>
  </ContextMenu>
);
export const CropExplorerMain = ({
  cropExploration,
  isAdmin,
}: {
  cropExploration: CropExplorationResponse | CropExplorationTemplateResponse;
  isAdmin?: boolean;
}) => {
  const { t } = useTranslation("cropExplorer");

  const { mutate: updateCropExploration, isPending: isUpdatePending } = useUpdateCropExploration();
  const { mutate: updateCropExplorationTemplate, isPending: isTemplateUpdatePending } = useUpdateCropExplorationTemplate();
  const { mutate: deleteCropExploration, isPending: isDeletionPending } = useDeleteCropExploration(!!isAdmin);

  const [newName, setNewName] = useState<string>(cropExploration.name);
  const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showHectareModal, setShowHectareModal] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showZipCodeModal, setShowZipCodeModal] = useState<boolean>(false);
  const pseudoUserId = getLocalStorageCropExplorerPseudoUserId();

  if (!isAdmin) {
    setLocalStorageCountry((cropExploration as CropExplorationResponse).country);
    setLocalStorageZipCode((cropExploration as CropExplorationResponse).zipCode);
    setLocalStorageHectare((cropExploration as CropExplorationResponse).hectares);
  }

  const onDelete = () => {
    deleteCropExploration({ id: cropExploration.id, pseudoUserId });
    setShowContextMenu(false);
  };

  const onRenameName = (isAdmin: boolean) => {
    if (isAdmin) {
      updateCropExplorationTemplate({
        id: cropExploration.id,
        cropExploration: { ...cropExploration, name: newName } as CropExplorationTemplateResponse,
      });
      return;
    }

    updateCropExploration({ id: cropExploration.id, cropExploration: { ...cropExploration, pseudoUserId, name: newName } as CropExplorationResponse });
  };

  const onHectareModalFinish = (hectares: number | null) => {
    updateCropExploration({ id: cropExploration.id, cropExploration: { ...cropExploration, pseudoUserId, hectares } as CropExplorationResponse });
    setShowHectareModal(false);
  };

  const onZipCodeModalFinish = (zipCode: string) => {
    setLocalStorageZipCode(zipCode);
    setShowZipCodeModal(false);
    updateCropExploration({ id: cropExploration.id, cropExploration: { ...cropExploration, pseudoUserId, zipCode } as CropExplorationResponse });
  };

  useEffect(() => {
    if (isAdmin) {
      if (isTemplateUpdatePending) setShowRenameModal(false);
      return;
    }

    if (isUpdatePending) setShowRenameModal(false);
  }, [isUpdatePending, isTemplateUpdatePending, isAdmin]);

  return (
    <Flex data-testid={`tabs-tab-content-${cropExploration.id}`} className="crop-explorer tab-content" vertical={true}>
      <Flex justify={!isAdmin ? "space-between" : "flex-end"} className="margin-left-100 margin-right-100">
        {!isAdmin && (
          <Flex gap={16} align="center" className="padding-bottom-100" style={{ width: "16rem" }}>
            <div data-testid="crop-explorer-tab-zip-code-input" onClick={() => setShowZipCodeModal(true)}>
              <InputWithAddonAfter
                addonAfter={<Icon iconName="tabler/map-pin" size="s" />}
                placeholder={t("project.inputs.zipCodePlaceholder")}
                style={{ pointerEvents: "none" }}
                value={(cropExploration as CropExplorationResponse).zipCode}
              />
            </div>
            <div data-testid="crop-explorer-tab-hectare-input" onClick={() => setShowHectareModal(true)}>
              <InputWithAddonAfter
                addonAfter={t("project.inputs.areaSuffix")}
                placeholder={t("project.inputs.areaPlaceholder")}
                style={{ pointerEvents: "none" }}
                value={(cropExploration as CropExplorationResponse).hectares}
              />
            </div>
          </Flex>
        )}
        <CropExplorerContextMenu
          visible={showContextMenu}
          onDelete={() => setShowDeleteModal(true)}
          handleShowContextMenu={setShowContextMenu}
          onRename={() => setShowRenameModal(true)}
          t={t}
        />
      </Flex>
      <Flex className="crop-explorer content">
        <CropExplorerTimeline cropExploration={cropExploration} isAdmin={isAdmin} />
      </Flex>
      <Form>
        <Modal
          input={{
            initialValue: cropExploration.name,
            label: `${t("project.renameProjectModal.inputLabel")}`,
            onChange: (e) => setNewName(e.target.value),
            name: "name",
            rules: [{ required: true }],
          }}
          title={t("project.renameProjectModal.title")}
          okText={t("project.renameProjectModal.confirm")}
          cancelText={t("project.renameProjectModal.cancel")}
          onOk={() => onRenameName(!!isAdmin)}
          loading={isUpdatePending}
          showModal={showRenameModal}
          onCancel={() => setShowRenameModal(false)}
          destroyOnHidden={true}
          okButtonProps={{ disabled: newName.length === 0 }}
        />
      </Form>
      <Modal
        title={t("project.deleteProjectModal.title", { projectName: cropExploration.name })}
        content={<p className="paragraph-m">{t("project.deleteProjectModal.content")}</p>}
        okText={t("project.deleteProjectModal.confirm")}
        cancelText={t("project.deleteProjectModal.cancel")}
        onOk={onDelete}
        loading={isDeletionPending}
        showModal={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        destroyOnHidden={true}
      />
      <>
        <HectareModal
          existingHectares={(cropExploration as CropExplorationResponse).hectares}
          open={showHectareModal}
          onClose={() => setShowHectareModal(false)}
          onFinish={onHectareModalFinish}
        />
        <ZipCodeModal
          existingZipCode={(cropExploration as CropExplorationResponse).zipCode}
          open={showZipCodeModal}
          onClose={() => setShowZipCodeModal(false)}
          onFinish={onZipCodeModalFinish}
        />
      </>
    </Flex>
  );
};
